import Animation from '@/editor/assets/animation'
import { factory } from '@/store/project/state'
import { fromStorage } from '@/etc/storage'
import {
  rawScaleToFitPercent,
  readStripFitScaleBaseline,
} from '@/editor/transform/fitScale'
import {
  TRANSFORM_KEYFRAME_USER_PRESETS_KEY,
  buildTransformPresetSummaries,
} from '@/editor/transform/transformKeyframePresets'

const TRANSFORM_PROP_AXIS = {
  'position.x': ['position', 'x'],
  'position.y': ['position', 'y'],
  'position.z': ['position', 'z'],
  'rotation.x': ['rotation', 'x'],
  'rotation.y': ['rotation', 'y'],
  'rotation.z': ['rotation', 'z'],
  'scale.x': ['scale', 'x'],
  'scale.y': ['scale', 'y'],
  'scale.z': ['scale', 'z'],
}

const clamp = (value, min, max) => Math.min(max, Math.max(min, value))

const finite = (value, fallback = 0) => {
  const num = Number(value)
  return Number.isFinite(num) ? num : fallback
}

const toFrameCount = (seconds, fps) =>
  Math.max(1, Math.round(finite(seconds, 0) * Math.max(1, finite(fps, 24))))

const toFrameIndex = (seconds, fps, clipFrameCount) =>
  clamp(
    Math.round(finite(seconds, 0) * Math.max(1, finite(fps, 24))),
    0,
    Math.max(0, clipFrameCount - 1)
  )

const frameToLocalTime = (frame, fps, stripLength) =>
  clamp(
    finite(frame, 0) / Math.max(1, finite(fps, 24)),
    0,
    Math.max(0, finite(stripLength, 0))
  )

const radToDeg = (value) => (finite(value, 0) * 180) / Math.PI

const makeProbeMesh = () => ({
  text: '',
  color: '',
  fontSize: 0,
  visible: true,
  position: {
    x: 0,
    y: 0,
    z: 0,
    set(x, y, z) {
      this.x = x
      this.y = y
      this.z = z
    },
  },
  rotation: {
    x: 0,
    y: 0,
    z: 0,
  },
  scale: {
    x: 1,
    y: 1,
    z: 1,
    set(x, y, z) {
      this.x = x
      this.y = y
      this.z = z
    },
  },
  material: {
    opacity: 1,
    rotation: 0,
    needsUpdate: false,
    map: {
      needsUpdate: false,
    },
  },
  layers: {
    set() {},
  },
})

const normalizeTransformMode = (asset) => {
  if (!asset) return '2d'
  if (Number(asset.D) >= 3) return '3d'
  if (typeof asset.isType === 'function' && asset.isType('d3')) return '3d'
  return '2d'
}

const normalizeTransform = (props = {}, scaleBaseline = null) => {
  const baseline = scaleBaseline || readStripFitScaleBaseline(null)
  return {
  position: {
    x: finite(props['position.x'], 0),
    y: finite(props['position.y'], 0),
    z: finite(props['position.z'], 0),
  },
  scalePct: {
    x: rawScaleToFitPercent(finite(props['scale.x'], 1), baseline.x),
    y: rawScaleToFitPercent(finite(props['scale.y'], 1), baseline.y),
    z: rawScaleToFitPercent(finite(props['scale.z'], 1), baseline.z),
  },
  rotationDeg: {
    x: radToDeg(props['rotation.x']),
    y: radToDeg(props['rotation.y']),
    z: radToDeg(props['rotation.z']),
  },
  }
}

const buildTransformKeyframeIndex = (strip, fps) => {
  const index = {
    position: { x: [], y: [], z: [] },
    rotation: { x: [], y: [], z: [] },
    scale: { x: [], y: [], z: [] },
  }

  const pushUnique = (bucket, frame) => {
    if (!bucket.includes(frame)) bucket.push(frame)
  }

  const keyframes = Array.isArray(strip?.animation?.keyframes)
    ? strip.animation.keyframes
    : []
  const clipFrameCount = toFrameCount(strip?.length, fps)

  keyframes.forEach((kf) => {
    const frame = toFrameIndex(kf?.time, fps, clipFrameCount)
    const props = kf?.props || {}
    Object.keys(props).forEach((code) => {
      const path = TRANSFORM_PROP_AXIS[code]
      if (!path) return
      const [group, axis] = path
      pushUnique(index[group][axis], frame)
    })
  })

  Object.keys(index).forEach((group) => {
    Object.keys(index[group]).forEach((axis) => {
      index[group][axis].sort((a, b) => a - b)
    })
  })

  return index
}

const buildExistingTransformKeyframes = (strip, fps) => {
  const keyframes = Array.isArray(strip?.animation?.keyframes)
    ? strip.animation.keyframes
    : []
  const clipFrameCount = toFrameCount(strip?.length, fps)

  return keyframes
    .map((kf) => {
      const props = Object.keys(kf?.props || {}).filter(
        (code) => !!TRANSFORM_PROP_AXIS[code]
      )
      if (!props.length) return null
      return {
        frame: toFrameIndex(kf?.time, fps, clipFrameCount),
        props,
      }
    })
    .filter(Boolean)
}

const buildTransformPresetStateSummaries = () => {
  try {
    return buildTransformPresetSummaries(
      fromStorage(TRANSFORM_KEYFRAME_USER_PRESETS_KEY, {})
    )
  } catch (e) {
    return []
  }
}

const normalizeFrameRange = (range, clipFrameCount) => {
  const fallback = {
    startFrame: 0,
    endFrameExclusive: clipFrameCount,
  }
  if (!range || typeof range !== 'object') return fallback

  const maxFrame = Math.max(0, clipFrameCount - 1)
  const rawStart = finite(range.startFrame, 0)
  const rawEndExclusive = finite(range.endFrameExclusive, clipFrameCount)
  const startFrame = clamp(Math.round(rawStart), 0, maxFrame)
  const endFrameExclusive = clamp(
    Math.round(rawEndExclusive),
    startFrame + 1,
    clipFrameCount
  )

  return {
    startFrame,
    endFrameExclusive,
  }
}

const findStrip = (projectState, stripId = null) => {
  const targetId = stripId || projectState?.timeline?.activeStrip
  if (!targetId) return null

  const trackId = Object.keys(projectState?.tracks || {}).find(
    (id) => projectState.tracks[id]?.strips?.[targetId]
  )
  if (!trackId) return null

  return {
    trackId,
    strip: projectState.tracks[trackId].strips[targetId],
  }
}

const evaluateTransformAtFrame = ({
  strip,
  asset,
  frameIndex,
  fps,
}) => {
  const baseValues =
    strip?.animation && typeof strip.animation.values === 'object'
      ? strip.animation.values
      : asset?.defaultProps || {}
  const scaleBaseline = readStripFitScaleBaseline(strip, {
    x: baseValues['scale.x'],
    y: baseValues['scale.y'],
    z: baseValues['scale.z'],
  })
  const animation = new Animation(strip?.animation || { keyframes: [] }, baseValues)
  const probeMesh = makeProbeMesh()
  const localTime = frameToLocalTime(frameIndex, fps, strip?.length)
  const absTime = finite(strip?.start, 0) + localTime
  const frame = {
    strip,
    animation,
    mesh: probeMesh,
    material: probeMesh.material,
    controller: null,
  }

  let props = {}
  try {
    props = asset?.updateFrame(absTime, localTime, frame) || {}
  } catch (e) {
    props = animation.propsAt(localTime) || {}
  }

  return normalizeTransform(
    {
      ...baseValues,
      ...props,
    },
    scaleBaseline
  )
}

export const resolveTransformFrameRange = ({
  state,
  startPolicy = 'current_frame',
  rangePolicy = 'selected_range',
} = {}) => {
  if (!state) return null

  const clipFrameCount = Math.max(1, Math.round(finite(state.clipFrameCount, 1)))
  const fullClip = { startFrame: 0, endFrameExclusive: clipFrameCount }
  const selectedRange = normalizeFrameRange(state.selectedFrameRange, clipFrameCount)
  const currentFrame = clamp(
    Math.round(finite(state.currentClipFrame, 0)),
    0,
    clipFrameCount - 1
  )

  const startFrame =
    startPolicy === 'clip_start'
      ? 0
      : startPolicy === 'range_start'
        ? selectedRange.startFrame
        : currentFrame

  const endFrameExclusive =
    rangePolicy === 'full_clip'
      ? fullClip.endFrameExclusive
      : rangePolicy === 'current_to_clip_end'
        ? fullClip.endFrameExclusive
        : rangePolicy === 'current_to_range_end'
          ? Math.max(currentFrame + 1, selectedRange.endFrameExclusive)
          : selectedRange.endFrameExclusive

  const normalized = normalizeFrameRange(
    {
      startFrame,
      endFrameExclusive,
    },
    clipFrameCount
  )

  return {
    ...normalized,
    frameSpan: Math.max(1, normalized.endFrameExclusive - normalized.startFrame),
  }
}

export const buildTransformAgentState = ({
  projectState,
  stripId = null,
  frameRange = null,
} = {}) => {
  const selected = findStrip(projectState, stripId)
  if (!selected || !selected.strip) return null

  const { strip } = selected
  const fps = Math.max(1, Math.round(finite(projectState?.settings?.fps, 24)))
  const clipFrameCount = toFrameCount(strip?.length, fps)
  const currentTimeAbs = finite(projectState?.timeline?.currentTime, strip?.start)
  const currentLocalTime = clamp(
    currentTimeAbs - finite(strip?.start, 0),
    0,
    Math.max(0, finite(strip?.length, 0))
  )
  const currentClipFrame = clamp(
    Math.round(currentLocalTime * fps),
    0,
    clipFrameCount - 1
  )
  const asset = factory.asset(strip?.asset?.uuid)
  const transformMode = normalizeTransformMode(asset)
  const projectAspectRatio = `${projectState?.settings?.aspect?.w || 1}:${projectState?.settings?.aspect?.h || 1}`
  const selectedFrameRange = normalizeFrameRange(
    frameRange ||
      (strip?.clipRange?.enabled
        ? {
            startFrame: strip.clipRange.startFrame,
            endFrameExclusive: strip.clipRange.endFrameExclusive,
          }
        : null),
    clipFrameCount
  )

  return {
    selectedStripId: strip.uuid,
    clipFrameCount,
    currentClipFrame,
    assetType: asset?.type || strip?.asset?.type || 'unknown',
    transformMode,
    projectAspectRatio,
    selectedFrameRange,
    transformKeyframeIndex: buildTransformKeyframeIndex(strip, fps),
    existingTransformKeyframes: buildExistingTransformKeyframes(strip, fps),
    globalPresetSummaries: buildTransformPresetStateSummaries(),
    startFrameTransform: evaluateTransformAtFrame({
      strip,
      asset,
      frameIndex: 0,
      fps,
    }),
    currentFrameTransform: evaluateTransformAtFrame({
      strip,
      asset,
      frameIndex: currentClipFrame,
      fps,
    }),
  }
}

export const buildTransformAtFrame = ({
  projectState,
  stripId = null,
  frame = 0,
} = {}) => {
  const selected = findStrip(projectState, stripId)
  if (!selected || !selected.strip) return null

  const { strip } = selected
  const asset = factory.asset(strip?.asset?.uuid)
  const fps = Math.max(1, Math.round(finite(projectState?.settings?.fps, 24)))
  const clipFrameCount = toFrameCount(strip?.length, fps)
  const frameIndex = clamp(
    Math.round(finite(frame, 0)),
    0,
    clipFrameCount - 1
  )

  return evaluateTransformAtFrame({
    strip,
    asset,
    frameIndex,
    fps,
  })
}
